import { api } from '../api';
import { gameLang } from '../i18n/gameLang';

const LAUNCH_TTL_MS = 45000;
const launchCache = new Map();
let pageImportPromise = null;

function preferBackupFlag() {
  try {
    return window.localStorage.getItem('casinoPreferBackup') === '1';
  } catch {
    return false;
  }
}

export function launchCacheKey({
  gameId,
  isDemo,
  userId,
  lang,
  preferBackup,
  preferAggregator = '',
  epoch = 0,
}) {
  return [
    isDemo ? 'd' : 'r',
    userId || 0,
    gameId,
    lang,
    preferBackup ? 1 : 0,
    String(preferAggregator || '').toLowerCase() || '-',
    epoch,
  ].join('|');
}

/** Drop cached launch URLs (e.g. after FS activate so Nexus→Drakon remap is not stale). */
export function clearLaunchUrlCache(gameId = null) {
  if (gameId == null || gameId === '') {
    launchCache.clear();
    return;
  }
  const needle = `|${String(gameId)}|`;
  for (const key of [...launchCache.keys()]) {
    if (key.includes(needle)) launchCache.delete(key);
  }
}

export function peekLaunchUrl(opts) {
  const key = launchCacheKey(opts);
  const hit = launchCache.get(key);
  if (!hit) return null;
  if (Date.now() - hit.at > LAUNCH_TTL_MS) {
    launchCache.delete(key);
    return null;
  }
  return hit.url;
}

export function rememberLaunchUrl(opts, url) {
  if (!url) return;
  launchCache.set(launchCacheKey(opts), { url, at: Date.now() });
}

export function prefetchCasinoGameChunk() {
  if (!pageImportPromise) {
    pageImportPromise = import('../pages/CasinoGamePage').catch(() => {
      pageImportPromise = null;
    });
  }
  return pageImportPromise;
}

export function prefetchCasinoLaunch({
  gameId,
  isDemo,
  token,
  locale,
  userId,
  preferAggregator = null,
}) {
  if (!gameId) return null;
  if (!isDemo && !token) return null;
  prefetchCasinoGameChunk();
  const lang = gameLang(locale);
  const preferBackup = preferBackupFlag();
  let preferAgg = preferAggregator ? String(preferAggregator).toLowerCase() : '';
  if (!preferAgg) {
    try {
      const fromSs = sessionStorage.getItem('vb_prefer_aggregator');
      const preferGame = sessionStorage.getItem('vb_prefer_game_id');
      if (fromSs && (!preferGame || String(preferGame) === String(gameId))) {
        preferAgg = String(fromSs).toLowerCase();
      }
    } catch {
      void 0;
    }
  }
  const opts = {
    gameId: String(gameId),
    isDemo: Boolean(isDemo),
    userId,
    lang,
    preferBackup,
    preferAggregator: preferAgg,
    epoch: 0,
  };
  const cached = peekLaunchUrl(opts);
  if (cached) return Promise.resolve(cached);
  const lobbyUrl = `${window.location.origin}/casino/game/${opts.gameId}`;
  const body = { gameId: opts.gameId, lang, lobbyUrl, preferBackup };
  if (preferAgg) body.preferAggregator = preferAgg;
  const req = isDemo
    ? api.launchCasinoDemo(body)
    : api.launchCasinoGame(token, body);
  return req
    .then((res) => {
      const url = res?.url || null;
      if (url) rememberLaunchUrl(opts, url);
      return url;
    })
    .catch(() => null);
}

export { preferBackupFlag };
